import { ChevronLeftIcon, ChevronRightIcon, DownloadIcon, ExternalLinkIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { FileRef } from '@/shared/api/types';
import { useFormatters } from '@/shared/i18n/format';
import { getFileKind } from '@/shared/lib/files';
import { Button } from '@/shared/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/shared/ui/dialog';
import { Spinner } from '@/shared/ui/spinner';

import { FileIcon } from './FileIcon';
import { toDownloadUrl } from './urls';

const TEXT_PREVIEW_LIMIT = 200_000;

function TextPreview({ url }: { url: string }) {
  const { t } = useTranslation('shared');
  const [state, setState] = useState<{ url: string; text?: string; error?: boolean }>({ url });
  useEffect(() => {
    const controller = new AbortController();
    fetch(url, { signal: controller.signal })
      .then(async (r) => {
        if (!r.ok) throw new Error(String(r.status));
        const text = await r.text();
        setState({
          url,
          text: text.length > TEXT_PREVIEW_LIMIT ? text.slice(0, TEXT_PREVIEW_LIMIT) + '\n…' : text,
        });
      })
      .catch(() => {
        if (!controller.signal.aborted) setState({ url, error: true });
      });
    return () => controller.abort();
  }, [url]);
  if (state.error) return <p className="p-6 text-sm text-muted-foreground">{t('files.previewFailed')}</p>;
  if (state.text === undefined || state.url !== url)
    return <Spinner className="m-10 size-6" label={t('states.loading')} />;
  return (
    <pre className="max-h-full w-full overflow-auto p-5 font-mono text-xs leading-relaxed whitespace-pre-wrap">
      {state.text}
    </pre>
  );
}

export interface FilePreviewDialogProps {
  files: readonly FileRef[];
  /** Index of the file to show; null closes the dialog. */
  index: number | null;
  onIndexChange: (index: number | null) => void;
}

/** Lightbox for attachments: images, PDF, video, audio and text; ←/→ switch between files. */
export function FilePreviewDialog({ files, index, onIndexChange }: FilePreviewDialogProps) {
  const { t } = useTranslation('shared');
  const fmt = useFormatters();
  const file = index === null ? undefined : files[index];
  const kind = file ? getFileKind(file.name, file.contentType) : 'other';
  const hasPrev = index !== null && index > 0;
  const hasNext = index !== null && index < files.length - 1;

  return (
    <Dialog open={!!file} onOpenChange={(open) => !open && onIndexChange(null)}>
      <DialogContent
        size="xl"
        className="flex h-[min(88dvh,56rem)] flex-col gap-0 overflow-hidden p-0"
        onKeyDown={(e) => {
          if (index === null) return;
          if (e.key === 'ArrowLeft' && hasPrev) onIndexChange(index - 1);
          if (e.key === 'ArrowRight' && hasNext) onIndexChange(index + 1);
        }}
      >
        {file && (
          <>
            <div className="flex items-center gap-3 border-b py-3 pr-14 pl-4">
              <FileIcon name={file.name} contentType={file.contentType} size="sm" />
              <div className="min-w-0 flex-1">
                <DialogTitle className="truncate text-sm">{file.name}</DialogTitle>
                <DialogDescription className="text-xs">
                  {fmt.fileSize(file.size)}
                  {files.length > 1 && index !== null && ` · ${index + 1} / ${files.length}`}
                </DialogDescription>
              </div>
              <Button variant="ghost" size="icon-sm" asChild aria-label={t('files.openInNewTab')}>
                <a href={file.url} target="_blank" rel="noopener noreferrer">
                  <ExternalLinkIcon />
                </a>
              </Button>
              <Button variant="ghost" size="icon-sm" asChild aria-label={t('files.download')}>
                <a href={toDownloadUrl(file.url)} download={file.name}>
                  <DownloadIcon />
                </a>
              </Button>
            </div>
            <div className="relative flex min-h-0 flex-1 items-center justify-center bg-muted/40">
              {kind === 'image' && (
                <img src={file.url} alt={file.name} className="max-h-full max-w-full object-contain" />
              )}
              {kind === 'pdf' && (
                <iframe src={file.url} title={file.name} className="size-full border-0 bg-white" />
              )}
              {kind === 'video' && (
                <video src={file.url} controls className="max-h-full max-w-full">
                  <track kind="captions" />
                </video>
              )}
              {kind === 'audio' && <audio src={file.url} controls className="w-full max-w-md" />}
              {kind === 'text' && <TextPreview url={file.url} />}
              {!['image', 'pdf', 'video', 'audio', 'text'].includes(kind) && (
                <div className="flex flex-col items-center gap-3 p-10 text-center">
                  <FileIcon name={file.name} contentType={file.contentType} size="lg" />
                  <p className="text-sm text-muted-foreground">{t('files.noPreview')}</p>
                  <Button asChild>
                    <a href={toDownloadUrl(file.url)} download={file.name}>
                      <DownloadIcon />
                      {t('files.download')}
                    </a>
                  </Button>
                </div>
              )}
              {hasPrev && (
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute left-3 rounded-full shadow-md"
                  onClick={() => onIndexChange(index - 1)}
                  aria-label={t('files.previous')}
                >
                  <ChevronLeftIcon />
                </Button>
              )}
              {hasNext && (
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute right-3 rounded-full shadow-md"
                  onClick={() => onIndexChange(index + 1)}
                  aria-label={t('files.next')}
                >
                  <ChevronRightIcon />
                </Button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
